import { useState, useEffect, useRef, useCallback } from 'react';
import { api, endpoints } from '../utils/api';

// Simple data fetching hook (without react-query for compatibility)
const useQuery = (queryKey, queryFn, options = {}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const queryFnRef = useRef(queryFn);
  const hasFetched = useRef(false);

  // Update the ref when queryFn changes
  queryFnRef.current = queryFn;

  const refetch = useCallback(async () => {
    if (options.enabled === false) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const result = await queryFnRef.current();
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [options.enabled]);

  // Only fetch on mount or when enabled changes
  useEffect(() => {
    if (options.enabled === false) {
      setIsLoading(false);
      return;
    }

    // For hooks with dynamic keys (like useForecast with sku), refetch when key changes
    const keyString = JSON.stringify(queryKey);
    if (!hasFetched.current || hasFetched.current !== keyString) {
      hasFetched.current = keyString;
      refetch();
    }
  }, [JSON.stringify(queryKey), options.enabled, refetch]);

  return { data, isLoading, error, refetch };
};

export const useDashboard = () => {
  return useQuery(
    ['dashboard'],
    () => api.get(endpoints.dashboard)
  );
};

export const useInventory = () => {
  return useQuery(
    ['inventory'],
    () => api.get(endpoints.inventory)
  );
};

export const useForecasts = () => {
  return useQuery(
    ['forecasts'],
    () => api.get(endpoints.forecasts)
  );
};

export const useForecast = (sku) => {
  return useQuery(
    ['forecast', sku],
    () => api.get(endpoints.forecast(sku)),
    { enabled: !!sku }
  );
};

export const useReorder = () => {
  return useQuery(
    ['reorder'],
    () => api.get(endpoints.reorder)
  );
};

export const useSalesAnalytics = (period = 'weekly') => {
  return useQuery(
    ['salesAnalytics', period],
    () => api.get(`${endpoints.salesAnalytics}?period=${period}`)
  );
};

export const useChannelAnalytics = () => {
  return useQuery(
    ['channelAnalytics'],
    () => api.get(endpoints.channelAnalytics)
  );
};

export const useBrandAnalytics = () => {
  return useQuery(
    ['brandAnalytics'],
    () => api.get(endpoints.brandAnalytics)
  );
};

export const usePromos = () => {
  return useQuery(
    ['promos'],
    () => api.get(endpoints.promos)
  );
};
